/**
 * F-17-type AUTHORING: the `/` menu inside a cell's text.
 *
 * Sibling of ui/text-format.ts, and here for the same reason — there are TWO
 * places a writer can type into a cell (the grid's cell editor, F-12, and the
 * script block, F-28) and they must agree on every answer: the same trigger,
 * the same ranking, the same idea of what picking one does to the text.
 *
 * The shape deliberately mirrors the comment composer's `@` picker
 * (features/comments/mentions.ts) — same trigger rule, same prefix-then-
 * substring ranking — because a writer who has learned one has learned both.
 *
 * Since 2026-09-04 the kinds are not only the two built-ins: an admin can
 * add more (a keyword + a style — features/text-kinds), and the menu is
 * DERIVED from that registry (`textTypeOptions`). A custom kind behaves
 * exactly like Sound or Visual, drawn in `‹ ›` instead of `[ ]`.
 *
 * The menu offers two things, and they do OPPOSITE things to the text:
 *
 *  - a KIND (Narration / Sound / Visual) REMOVES text. The kind is a switch
 *    point stored on the cell (`@kola/shared` text-types.ts), not characters
 *    in the value, so picking one deletes the `/so` the writer typed and
 *    leaves the line exactly as it was, with "sound from here on" recorded
 *    where the `/` stood. A cell can switch as often as the writer likes —
 *    `[a man enters] [door slams] He says hi` is one cell, typed in one go;
 *  - a SNIPPET (`/pause` → `[Pause]`) INSERTS text, exactly as `@kim` does.
 *    It is prose the script should carry — a spreadsheet, a translation and a
 *    CSV all see it — so it is written into the value and nothing else.
 *
 * PURE — no DOM, no Yjs.
 */
import {
  isBuiltinTextType,
  type CellTextType,
  type TextKind,
  type TextSegment,
} from "@kola/shared";

/**
 * A pickable line kind. `type: null` is narration — the default, stored as
 * the ABSENCE of any switch (text-types.ts), which is why the value here is
 * nullable rather than a third string.
 */
export interface TextTypeKindOption {
  kind: "type";
  type: CellTextType | null;
  /** What the picker row shows, and what the query matches against. */
  label: string;
  /** One-line hint of how the line will read. */
  hint: string;
}

/** A pickable snippet: `text` replaces the command, verbatim. */
export interface TextSnippetOption {
  kind: "snippet";
  text: string;
  label: string;
  hint: string;
}

export type TextTypeOption = TextTypeKindOption | TextSnippetOption;

/**
 * The snippets, in the order they are offered. ONE table on purpose — adding
 * an expression is adding a row here, and both editing surfaces get it.
 * `label` is what `/…` matches; `text` is inserted as typed here, brackets
 * and all, because unlike a kind's brackets these are content.
 */
export const TEXT_SNIPPETS: readonly { label: string; text: string }[] = [
  { label: "Pause", text: "[Pause]" },
  { label: "Impact", text: "[HA!PPY!]" },
];

/** The built-in kinds, in the order the menu offers them. */
const BUILTIN_KIND_OPTIONS: readonly TextTypeKindOption[] = [
  { kind: "type", type: null, label: "Narration", hint: "Plain text from here" },
  { kind: "type", type: "sound", label: "Sound", hint: "Green italic in [ ], from here" },
  { kind: "type", type: "visual", label: "Visual", hint: "Red semi-bold in [ ], from here" },
];

const SNIPPET_OPTIONS: readonly TextSnippetOption[] = TEXT_SNIPPETS.map(
  (snippet): TextSnippetOption => ({
    kind: "snippet",
    text: snippet.text,
    label: snippet.label,
    hint: `Adds ${snippet.text}`,
  }),
);

/**
 * The menu's hint for an admin-defined kind — what the writer will see, in
 * the words the built-ins' hints use ("Green italic in [ ], from here").
 * The ink is a swatch on the row, so the hint names only the weight/slant.
 */
export function textKindHint(kind: Pick<TextKind, "italic" | "bold">): string {
  const style =
    kind.italic && kind.bold
      ? "Italic semi-bold"
      : kind.italic
        ? "Italic"
        : kind.bold
          ? "Semi-bold"
          : "Styled";
  return `${style} in ‹ ›, from here`;
}

/**
 * The menu for a given registry of admin-defined kinds, in the order it is
 * offered: the built-in kinds first (Narration leads because it is the
 * default and because "put this line back to normal" is the command a writer
 * reaches for most after trying the other two), then the workspace's own
 * kinds in the order the admin added them, then the snippets.
 *
 * Derived, never cached across registries: the editors call this with the
 * live list (features/text-kinds/store.ts), so a kind an admin just added is
 * in the next `/` a writer types.
 */
export function textTypeOptions(kinds: readonly TextKind[] = []): TextTypeOption[] {
  return [
    ...BUILTIN_KIND_OPTIONS,
    ...kinds.map(
      (kind): TextTypeKindOption => ({
        kind: "type",
        type: kind.id,
        label: kind.label,
        hint: textKindHint(kind),
      }),
    ),
    ...SNIPPET_OPTIONS,
  ];
}

/** The menu with no admin-defined kinds — what shipped, and the tests' baseline. */
export const TEXT_TYPE_OPTIONS: readonly TextTypeOption[] = textTypeOptions();

/**
 * The `/` command the caret currently sits inside, or null.
 *
 * The `/` must start the text or follow whitespace, so a date ("12/03") and a
 * path ("audio/vo.wav") never open the menu mid-word. The query runs from
 * there to the caret and may not contain whitespace — the same rule the `@`
 * picker uses, and the reason a command closes as soon as the writer types a
 * space and carries on with the sentence.
 */
export function slashQueryAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("/");
  if (at < 0) return null;
  const preceding = at === 0 ? "" : before.charAt(at - 1);
  if (preceding !== "" && !/\s/.test(preceding)) return null;
  const query = before.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
}

/**
 * The options matching a query, prefix matches first ("na" → Narration).
 *
 * An empty query offers everything, so a bare `/` opens the whole menu. A
 * query that matches NOTHING returns nothing, and the caller closes the menu
 * rather than showing an empty box that would swallow Enter — "1/2 cups" must
 * stay ordinary text.
 */
export function matchTextTypes(
  query: string,
  options: readonly TextTypeOption[] = TEXT_TYPE_OPTIONS,
): TextTypeOption[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return [...options];
  const starts: TextTypeOption[] = [];
  const contains: TextTypeOption[] = [];
  for (const option of options) {
    const label = option.label.toLowerCase();
    if (label.startsWith(needle)) starts.push(option);
    else if (label.includes(needle)) contains.push(option);
  }
  return [...starts, ...contains];
}

/**
 * The text after taking `option`, and where the caret should land.
 *
 * A kind cuts the `/query` out and inserts nothing in its place: the command
 * IS the whole gesture, the switch lands at `start` (the caller stores it),
 * and the line the writer is typing must read the same after it as before.
 *
 * A snippet replaces the `/query` with its text plus a space — the space is
 * what the next word needs, and (as with `@handle `) what makes sure the
 * picker stays closed.
 */
export function applyTextTypePick(
  text: string,
  caret: number,
  start: number,
  option: TextTypeOption,
): { text: string; caret: number } {
  const inserted = option.kind === "snippet" ? `${option.text} ` : "";
  return {
    text: text.slice(0, start) + inserted + text.slice(caret),
    caret: start + inserted.length,
  };
}

/* ------------------------------------------------------------------ */
/* Rendering                                                           */
/* ------------------------------------------------------------------ */

/**
 * The class that paints one line kind, or undefined for narration.
 *
 * Worn per RUN — by each segment of a finished cell and of the editors'
 * mirror layers alike — but the CSS behind it is split: `.tt-<kind>` is
 * metric-neutral (ink, and a PAINTED semi-bold in the manner of `.tm-bold`)
 * so it is safe over a transparent textarea, while the italic lives under a
 * `.tt-shown` ancestor, which only the finished cell/block carries. See
 * ui/MarkedText.tsx on why width-changing styling cannot go near a mirror.
 */
export function textTypeClassName(type: CellTextType | undefined): string | undefined {
  if (type === undefined) return undefined;
  // A built-in has a hand-written rule in app.css (`tt-sound`, `tt-visual`);
  // an admin-defined kind's id is prefixed `k-`, and its rule is injected
  // from the registry (features/text-kinds/styles.ts) under `tt-k-<id>`.
  return `tt-${type}`;
}

/**
 * The brackets a run of `type` is drawn in. The built-ins keep the `[ ]`
 * they shipped with; an admin-defined kind gets `‹ ›`, which is what tells a
 * reader "a house kind, not a sound cue or a visual" without a legend — and
 * the same shape whether or not this client has loaded the kind's style.
 */
export function textTypeBrackets(type: CellTextType | undefined): [string, string] {
  return type !== undefined && !isBuiltinTextType(type) ? ["‹", "›"] : ["[", "]"];
}

/**
 * Whether the brackets of a sound/visual run open before segment `i` and
 * close after it. Segments come from `segmentMarkedText`, which cuts at run
 * edges, so a run's edge is always a change of `type` between neighbours —
 * and a bold phrase inside a sound cue shares the cue's type, so no bracket
 * ever lands mid-run.
 *
 * Drawn, never stored: switching a kind cannot rewrite text and a writer can
 * never half-delete a bracket.
 */
export function typeRunEdges(
  segments: readonly TextSegment[],
  i: number,
): { opens: boolean; closes: boolean } {
  const type = segments[i]?.type;
  if (type === undefined) return { opens: false, closes: false };
  return { opens: segments[i - 1]?.type !== type, closes: segments[i + 1]?.type !== type };
}

/** The little preview at the left of a menu row: its class and its glyph. */
export function textTypeSwatch(option: TextTypeOption): { className: string; glyph: string } {
  if (option.kind === "snippet") return { className: "tt-narration", glyph: "+" };
  if (option.type === null) return { className: "tt-narration", glyph: "Aa" };
  const [open, close] = textTypeBrackets(option.type);
  return { className: textTypeClassName(option.type) ?? "tt-narration", glyph: `${open} ${close}` };
}
